// @flow
import * as React from 'react';

import type { TextProps } from 'reshaped';

import { useEntityPermissions } from '@gandi/acl';
import type { Organization } from '@gandi/redux-organization';

import { useTranslator } from '@gandi/translate';

import {
  selectHasMissingOrgInfo,
  selectMissingOrgInfo,
} from 'src/modules/organization/selectors';
import { ORGA_WRITE } from 'src/modules/permissions';

import { useAppSelector } from './useAppSelector';

/** @private Used for unit tests only */
export type StrictOrganizationShape = {
  id: string,
  bu_id?: number,
  type?: Organization['type'],
  orgname?: string,
  firstname?: string,
  lastname?: string,
  streetaddr?: string,
  streetaddr2?: string,
  city?: string,
  /** Postal code */
  zip?: string,
  /** ISO-3166-1 alpha-2 country code */
  country?: string,
  /** ISO-3166-2 country subdivision code (e.g.: CA-QC) */
  state?: string,
  /** Siren / Company ID */
  siren?: string,
};
export type OrganizationShape = $ReadOnly<{
  ...StrictOrganizationShape,
  ...
}>;

export const sirenCountries = ['FR', 'MQ', 'GP', 'RE'];

export function displaySirenMessage(orga: OrganizationShape): boolean {
  return (
    orga.type !== 'individual' &&
    !('siren' in orga) &&
    sirenCountries.includes(orga.country)
  );
}
/**
 * Hook to retrieve missing information
 */
export function useMissingOrgInfo(
  orga?: ?OrganizationShape,
  enabled: boolean,
  displayVAT: boolean,
): {
  missingInfo: Array<{ msg: string, color?: TextProps<>['color'] }>,
  missingFields: Array<string>,
  hasMissingInfo: boolean,
  hasMissingFields: boolean,
} {
  const { __ } = useTranslator();

  // $FlowFixMe[incompatible-use]
  const { hasPermissions } = useEntityPermissions(orga.id, {
    enabled,
  });
  const hasPermission = hasPermissions([ORGA_WRITE]);

  const missingFields = useAppSelector(state =>
    orga ? selectMissingOrgInfo(state, { orgId: orga.id, orga }) : undefined,
  );

  const hasMissingOrgInfo = React.useMemo(
    () => (missingFields?.length || 0) > 0,
    [missingFields?.length],
  );

  return React.useMemo(() => {
    const missingInfo: Array<{ msg: string, color?: TextProps<>['color'] }> =
      [];

    if (displayVAT && orga?.type === 'company' && !('vat_number' in orga)) {
      missingInfo.push({ msg: __('No VAT number') });
    }

    if (displayVAT && orga && displaySirenMessage(orga)) {
      missingInfo.push({
        msg: __('Missing SIREN number, required as of September 1, 2026.'),
        color: 'warning',
      });
    }

    if (hasMissingOrgInfo) {
      if (hasPermission)
        missingInfo.push({
          msg: __('Required information will be asked afterwards'),
        });
      else
        missingInfo.push({
          msg: __(
            'Please contact the owner to complete the missing information.',
          ),
        });
    }

    return {
      missingInfo,
      missingFields: missingFields || [],
      hasMissingInfo: missingInfo.length > 0,
      hasMissingFields: (missingFields?.length || 0) > 0,
    };
  }, [displayVAT, orga, hasPermission, __, hasMissingOrgInfo, missingFields]);
}

export const useHasMissingOrgInfo = (
  orgId?: string,
  orga?: ?OrganizationShape,
): boolean => {
  return useAppSelector(state => {
    if (!orgId) return false;
    return !!selectHasMissingOrgInfo(state, { orgId, orga });
  });
};
